import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import lmsPaymentService from "services/lms/LMSLoanPaymentService";

export const initialState = {
    isLoading: false,
    isSuccess: false,
    isError: false,
    message: "",
    showMessage: false,
    redirect: "",
    token: localStorage.getItem(AUTH_TOKEN) || null,
    result: {},
    getPageSize: "",
    getPageIndex: "",
    getCount: "",
    statusRespon: 0,
    total: 0,

    loans: [],
    loanDetail: {},
    installmentSchedule: {},
    paymentHistory: [],
    paymentResult: null,
    installmentDue: {},
    paymentPriority: {}
};

export const fetchLoans = createAsyncThunk(
    'LMSLoanPayment/fetchLoans',
    async (payload, thunkAPI) => {
        try {
            const response = await lmsPaymentService.getLoans(payload);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchLoanDetail = createAsyncThunk(
    'LMSLoanPayment/fetchLoanDetail',
    async (payload, thunkAPI) => {
        try {
            return await lmsPaymentService.getLoanDetail(payload);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchInstallmentSchedule = createAsyncThunk(
    'LMSLoanPayment/fetchInstallmentSchedule',
    async (payload, thunkAPI) => {
        try {
            return await lmsPaymentService.getInstallmentSchedule(payload);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const submitPayment = createAsyncThunk(
    'LMSLoanPayment/submitPayment',
    async (payload, thunkAPI) => {
        try {
            return await lmsPaymentService.createPayment(payload);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchPaymentHistory = createAsyncThunk(
    'LMSLoanPayment/fetchPaymentHistory',
    async (applicationId, thunkAPI) => {
        try {
            return await lmsPaymentService.getPaymentHistory(applicationId);
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const fetchInstallmentDue = createAsyncThunk(
    'LMSLoanPayment/getInstallmentDue',
    async (payload, thunkAPI) => {
        try {
            return await lmsPaymentService.getInstallmentDue(payload)
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message);
        }
    }
)

export const fetchPaymentPriorities = createAsyncThunk(
    'LMSLoanPayment/getPaymentPriorityByDpd',
    async (payload, thunkAPI) => {
        try {
            return await lmsPaymentService.getPaymentPriorities(payload)
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response?.data || error.message)
        }
    }
)
const loanSlice = createSlice({
    name: 'LMSLoanPayment',
    initialState,
    reducers: {
        resetLoanState: (state) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = '';
            state.paymentResult = null;
        }
    },
    extraReducers: (builder) => {
        builder

            /* LIST LOANS */
            .addCase(fetchLoans.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchLoans.fulfilled, (state, action) => {
                state.isLoading = false;
                state.loans = action.payload.data;
                state.total = action.payload.count;
            })
            .addCase(fetchLoans.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            /* LOAN DETAIL */
            .addCase(fetchLoanDetail.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchLoanDetail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.loanDetail = action.payload.data;
            })
            .addCase(fetchLoanDetail.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            /* INSTALLMENT SCHEDULE */
            .addCase(fetchInstallmentSchedule.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchInstallmentSchedule.fulfilled, (state, action) => {
                state.isLoading = false;
                state.installmentSchedule = action.payload.data;
            })
            .addCase(fetchInstallmentSchedule.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            /* PAYMENT */
            .addCase(submitPayment.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(submitPayment.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.paymentResult = action.payload;
            })
            .addCase(submitPayment.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            /* PAYMENT HISTORY */
            .addCase(fetchPaymentHistory.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.paymentHistory = action.payload;
            })
            .addCase(fetchPaymentHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })

            // Angsuran jatuh tempo
            .addCase(fetchInstallmentDue.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchInstallmentDue.fulfilled, (state, action) => {
                state.isLoading = false;
                state.installmentDue = action.payload.data;
            })
            .addCase(fetchInstallmentDue.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                state.installmentDue = null;
            })

            // Payment priority by dpd
            .addCase(fetchPaymentPriorities.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaymentPriorities.fulfilled, (state, action) => {
                state.isLoading = false;
                state.paymentPriority = action.payload.data;
            })
            .addCase(fetchPaymentPriorities.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            });
    }
});

export const {
    showNotification,
    hideNotification,
    showLoading,
    resetLoanState
} = loanSlice.actions;

export default loanSlice.reducer;